/**
 * @file SiteSeoPage.tsx
 * @description Раздел «Сайт → SEO» (phase19.16, партия Р3в, v0.19.64;
 * SB145, SB131, SB132, SC14, SC45, SB104; uiux-r3 §3.5): страница без
 * канвы, на ширину колонки кабинета (SC55), вкладки «Основное ·
 * Переадресации (N) · Проверка сайта (N)»; вкладка — в адресе (`?tab=`), на
 * 360 ряд вкладок прокручивается.
 *
 * Права (SB55): «Основное» — право настроек магазина (без него вкладки нет);
 * «Переадресации» и «Проверку сайта» читает право контента или настроек,
 * переадресации пишет право настроек. Главное действие на вкладке
 * «Переадресации» — «+ Добавить переадресацию» (в шапке; до 1024 —
 * закреплено снизу в общей полосе каркаса).
 *
 * «SEO-техника» v0.19.65 (SB148; uiux-seo-tech §3.1): вкладка «Не найдено
 * (N)» между «Переадресациями» и «Проверкой сайта» (`?tab=notfound`; N —
 * необработанные нескрытые адреса за 7 дней); читает право контента или
 * настроек, переадресует и скрывает строки — право настроек. Главного
 * действия у вкладки нет.
 */

import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { SeoAuditTab } from "../../../components/site-seo/audit/SeoAuditTab";
import { SeoMainTab } from "../../../components/site-seo/main/SeoMainTab";
import { NotFoundTab } from "../../../components/site-seo/not-found/NotFoundTab";
import { RedirectsTab } from "../../../components/site-seo/redirects/RedirectsTab";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { Tabs, type TabItem } from "../../../components/ui/Tabs";
import { useNotFound } from "../../../hooks/site-seo/useNotFound";
import { useRedirects } from "../../../hooks/site-seo/useRedirects";
import { useSeoAudit } from "../../../hooks/site-seo/useSeoAudit";

type SeoTab = "main" | "redirects" | "notfound" | "audit";

const TAB_VALUES: readonly SeoTab[] = ["main", "redirects", "notfound", "audit"];

export function SiteSeoPage() {
  const canSettings = useHasPermission("SHOP_MANAGE_SETTINGS");
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const fallback: SeoTab = canSettings ? "main" : "redirects";
  const tab: SeoTab = TAB_VALUES.includes(asked as SeoTab) && (asked !== "main" || canSettings) ? (asked as SeoTab) : fallback;
  const inactiveAsked = params.get("status") === "inactive";
  const redirects = useRedirects(true, inactiveAsked);
  const notFound = useNotFound();
  const audit = useSeoAudit();
  const [creating, setCreating] = useState(false);
  const { setInactiveOnly } = redirects;

  // «Исправить →» из «Проверки сайта» ведёт сюда же с `status=inactive` — включить отбор.
  useEffect(() => {
    if (inactiveAsked) setInactiveOnly(true);
  }, [inactiveAsked, setInactiveOnly]);

  function select(next: SeoTab) {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set("tab", next);
      return out;
    }, { replace: true });
  }

  const issues = audit.audit ? audit.audit.counts.warning + audit.audit.counts.info : undefined;
  const items: TabItem<SeoTab>[] = [
    ...(canSettings ? [{ value: "main" as const, label: "Основное" }] : []),
    { value: "redirects", label: "Переадресации", count: redirects.summary?.total },
    { value: "notfound", label: "Не найдено", count: notFound.count },
    { value: "audit", label: "Проверка сайта", count: issues },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <CabinetPageHeader
        title="SEO"
        subtitle="Как сайт выглядит в поиске, переадресации со старых адресов и проверка"
        action={
          tab === "redirects" && canSettings ? (
            <Button variant="brand" onClick={() => setCreating(true)}>
              <Plus aria-hidden />
              Добавить переадресацию
            </Button>
          ) : undefined
        }
      />
      <Tabs aria-label="Разделы SEO" items={items} value={tab} onChange={select} />
      {tab === "main" ? <SeoMainTab /> : null}
      {tab === "redirects" ? <RedirectsTab api={redirects} canEdit={canSettings} creating={creating} onCreateClose={() => setCreating(false)} /> : null}
      {tab === "notfound" ? <NotFoundTab api={notFound} canEdit={canSettings} onRedirectsChanged={() => void redirects.reload()} /> : null}
      {tab === "audit" ? <SeoAuditTab api={audit} /> : null}
    </div>
  );
}
